import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Roles disponibles
export const ROLES = {
  GERENCIA: 'gerencia',
  ADMIN: 'admin',
  ASESOR: 'asesor',
  DISEÑADOR: 'diseñador',
  VENDEDOR: 'vendedor',
  ALIADO: 'aliado',
  COMERCIAL: 'comercial',
  CONTABILIDAD: 'contabilidad'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return response.data;
  }, []);

  const register = useCallback(async (username, email, password, role = 'vendedor') => {
    const response = await axios.post(`${API_URL}/auth/register`, { username, email, password, role });
    
    // Si la cuenta quedó pendiente, no guardar token
    if (response.data.status === 'pendiente') {
      return response.data;
    }
    
    // Si fue aprobado (primer usuario), guardar token normal
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    }
    return response.data;
  }, []);

  const logout = useCallback(() => {
    // Limpiar todo el almacenamiento local relacionado con la sesión
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    sessionStorage.clear();
    
    // Limpiar estado
    setToken(null);
    setUser(null);
    
    // Limpiar headers de axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Forzar recarga para limpiar cualquier estado en memoria
    window.location.href = '/login';
  }, []);

  // Helper para verificar permisos
  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  }, [user]);

  // Helper para verificar rol
  const isRole = useCallback((role) => {
    if (!user) return false;
    return user.role === role;
  }, [user]);

  // Helpers de roles específicos
  const isGerencia = useCallback(() => isRole(ROLES.GERENCIA), [isRole]);
  const isAdmin = useCallback(() => isRole(ROLES.ADMIN), [isRole]);
  const isAsesor = useCallback(() => isRole(ROLES.ASESOR), [isRole]);
  const isDiseñador = useCallback(() => isRole(ROLES.DISEÑADOR), [isRole]);

  // Verificar si puede administrar usuarios
  const canManageUsers = useCallback(() => hasPermission('manage_users'), [hasPermission]);

  // Verificar si puede ver costeo
  const canViewCosting = useCallback(() => hasPermission('view_costing'), [hasPermission]);

  // Verificar si puede crear fichas
  const canCreateSheets = useCallback(() => hasPermission('create_sheets'), [hasPermission]);

  // Verificar si puede eliminar fichas
  const canDeleteSheets = useCallback(() => hasPermission('delete_sheets'), [hasPermission]);

  // Verificar si puede descargar expediente
  const canDownloadExpediente = useCallback(() => hasPermission('download_expediente'), [hasPermission]);

  // Memoizar el valor del contexto para evitar re-renders innecesarios
  const contextValue = useMemo(() => ({ 
    user, 
    token, 
    login, 
    register, 
    logout, 
    loading,
    hasPermission,
    isRole,
    isGerencia,
    isAdmin,
    isAsesor,
    isDiseñador,
    canManageUsers,
    canViewCosting,
    canCreateSheets,
    canDeleteSheets,
    canDownloadExpediente
  }), [
    user, 
    token, 
    login, 
    register, 
    logout, 
    loading,
    hasPermission,
    isRole,
    isGerencia,
    isAdmin,
    isAsesor,
    isDiseñador,
    canManageUsers,
    canViewCosting,
    canCreateSheets,
    canDeleteSheets,
    canDownloadExpediente
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};